import type { TicketVisitReportDto } from '@evimo/client/models';
import type { KanbanTicketReportView } from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import { asOptionalString } from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import type { TicketVisitView } from './types';

function mapVisitReport(
  report: TicketVisitReportDto['report'],
): KanbanTicketReportView | null {
  if (report == null) return null;
  const raw = report as {
    executionResult?: string | null;
    summary?: string | null;
    workPerformed?: string | null;
    recommendation?: string | null;
    technicianComment?: string | null;
    faultCategory?: string | null;
    faultDescription?: string | null;
    confirmedSerialNumber?: string | null;
    confirmedAssetIdentifier?: string | null;
    pauseDurationMinutes?: number | null;
    arrivedAt?: string | Date | null;
  };
  const arrivedRaw = raw.arrivedAt;
  const arrivedAt =
    arrivedRaw == null
      ? null
      : arrivedRaw instanceof Date
        ? arrivedRaw
        : new Date(arrivedRaw);
  return {
    executionResult: asOptionalString(raw.executionResult) ?? null,
    summary: asOptionalString(raw.summary) ?? null,
    workPerformed: asOptionalString(raw.workPerformed) ?? null,
    recommendation: asOptionalString(raw.recommendation) ?? null,
    technicianComment: asOptionalString(raw.technicianComment) ?? null,
    faultCategory: asOptionalString(raw.faultCategory) ?? null,
    faultDescription: asOptionalString(raw.faultDescription) ?? null,
    confirmedSerialNumber: asOptionalString(raw.confirmedSerialNumber) ?? null,
    confirmedAssetIdentifier:
      asOptionalString(raw.confirmedAssetIdentifier) ?? null,
    pauseDurationMinutes:
      typeof raw.pauseDurationMinutes === 'number'
        ? raw.pauseDurationMinutes
        : null,
    arrivedAt:
      arrivedAt && !Number.isNaN(arrivedAt.getTime()) ? arrivedAt : null,
  };
}

export function mapTicketVisitDto(
  visit: TicketVisitReportDto,
): TicketVisitView {
  return {
    membershipId: visit.membershipId,
    workOrderId: visit.workOrderId,
    workOrderCode: visit.workOrderCode,
    executionNumber: visit.executionNumber,
    membershipStatus: visit.membershipStatus,
    completedAt: visit.completedAt ?? null,
    completedById: asOptionalString(visit.completedById) ?? null,
    completedByName: asOptionalString(visit.completedByName) ?? null,
    executionResult: asOptionalString(visit.executionResult) ?? null,
    report: mapVisitReport(visit.report),
  };
}

export function formatVisitCompletedAt(
  completedAt: Date | null,
  locale: string,
): string | null {
  if (!completedAt || Number.isNaN(completedAt.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(completedAt);
}
